import React, { useEffect, useMemo, useState } from "react";
import AppHeader from "../../components/AppHeader";
import { buildHierarchicalList } from "../../components/staff/constants";
import { fetchAttendanceMatrix, buildAttendanceMatrixExcelUrl } from "../../services/webReportsService";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { myanmarFontBase64 } from "../../utils/myanmarFontBase64";

export default function AttendanceMatrixReport() {
  const tenant = localStorage.getItem("tenant") || "";
  const token = sessionStorage.getItem("token") || "";

  // Filter States
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [branchId, setBranchId] = useState("");
  const [orgNodeId, setOrgNodeId] = useState("");
  const [displayMode, setDisplayMode] = useState("time"); // "time" or "presence"

  // Checkbox summary column states
  const [showTotalDays, setShowTotalDays] = useState(false);
  const [showExpectedDays, setShowExpectedDays] = useState(false);
  const [showPresentDays, setShowPresentDays] = useState(false);
  const [showLeaveDays, setShowLeaveDays] = useState(false);

  // Metadata Dropdown States
  const [branches, setBranches] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Data Grid States
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [dates, setDates] = useState([]);
  const [error, setError] = useState("");

  // Set default date range: first day of current month to today
  useEffect(() => {
    const pad = (n) => String(n).padStart(2, "0");
    const toKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);

    setDateFrom(toKey(firstDay));
    setDateTo(toKey(now));
  }, []);

  // Fetch branches and departments dropdown data on mount
  useEffect(() => {
    const fetchMetadata = async () => {
      const config = {
        withCredentials: true,
        headers: {
          "X-Tenant": tenant,
          "Authorization": `Bearer ${token}`
        }
      };
      try {
        const [branchRes, nodeRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_URL1}/som/branches`, config),
          axios.get(`${process.env.REACT_APP_API_URL1}/som/nodes`, config)
        ]);

        if (Array.isArray(branchRes.data?.data)) {
          setBranches(branchRes.data.data);
        }
        if (Array.isArray(nodeRes.data?.data)) {
          setDepartments(buildHierarchicalList(nodeRes.data.data));
        }
      } catch (err) {
        console.error("Failed to load metadata filters:", err);
      }
    };

    if (tenant && token) {
      fetchMetadata();
    }
  }, [tenant, token]);

  const canSearch = useMemo(() => Boolean(dateFrom && dateTo), [dateFrom, dateTo]);
  const canAutoLoad = useMemo(() => Boolean(tenant && dateFrom && dateTo), [tenant, dateFrom, dateTo]);

  const onSearch = async () => {
    if (!tenant) {
      setError("Missing tenant configuration.");
      return;
    }

    setError("");
    setLoading(true);
    try {
      const res = await fetchAttendanceMatrix({
        date_from: dateFrom,
        date_to: dateTo,
        branch_id: branchId || undefined,
        org_node_id: orgNodeId || undefined
      });

      if (res.success) {
        setDates(res.dates || []);
        setRows(res.rows || []);
      } else {
        throw new Error(res.message || "Failed to load report");
      }
    } catch (e) {
      setError(e?.message || "Failed to load report");
      setRows([]);
      setDates([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canAutoLoad) onSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAutoLoad]);

  const onExportExcel = async () => {
    if (!tenant) {
      setError("Missing tenant configuration.");
      return;
    }

    setError("");
    setLoading(true);
    try {
      const url = buildAttendanceMatrixExcelUrl({
        date_from: dateFrom,
        date_to: dateTo,
        branch_id: branchId || undefined,
        org_node_id: orgNodeId || undefined,
        show_total_days: showTotalDays,
        show_expected_days: showExpectedDays,
        show_present_days: showPresentDays,
        show_leave_days: showLeaveDays
      });

      const res = await fetch(url, {
        credentials: "include",
        headers: { "X-Tenant": tenant },
      });

      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `Export failed: HTTP ${res.status}`);
      }

      const blob = await res.blob();
      const a = document.createElement("a");
      const objectUrl = URL.createObjectURL(blob);
      a.href = objectUrl;
      a.download = `daily_attendance_matrix_${dateFrom}_${dateTo}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      setError(e?.message || "Export failed");
    } finally {
      setLoading(false);
    }
  };

  const onExportPDF = () => {
    try {
      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "legal" });

      if (myanmarFontBase64) {
        doc.addFileToVFS("Padauk-Regular.ttf", myanmarFontBase64);
        doc.addFont("Padauk-Regular.ttf", "Padauk", "normal");
        doc.addFont("Padauk-Regular.ttf", "Padauk", "bold");
      }
      const defaultFont = myanmarFontBase64 ? "Padauk" : "helvetica";

      doc.setFont(defaultFont, "bold");
      doc.setFontSize(16);
      doc.setTextColor(30, 41, 59);
      doc.text("Daily Attendance Matrix Report", 14, 15);

      doc.setFont(defaultFont, "normal");
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Period: ${dateFrom} to ${dateTo}`, 14, 21);

      // Build headers
      const tableHeaders = ["No.", "Employee ID", "Staff Name", "Dept"];
      dates.forEach(d => {
        const dayNum = d.split("-")[2];
        tableHeaders.push(dayNum);
      });

      if (showTotalDays) tableHeaders.push("Total Days");
      if (showExpectedDays) tableHeaders.push("Expected Days");
      if (showPresentDays) tableHeaders.push("Present Days");
      if (showLeaveDays) tableHeaders.push("Leave Days");

      // Build rows
      const tableRows = rows.map((r, index) => {
        const rowData = [
          index + 1,
          r.employee_id,
          r.full_name,
          r.dept_name
        ];

        dates.forEach(d => {
          const dayInfo = r.days[d];
          if (dayInfo) {
            if (dayInfo.type === "present" && displayMode === "presence") {
              rowData.push("✔");
            } else {
              rowData.push(dayInfo.text || "-");
            }
          } else {
            rowData.push("-");
          }
        });

        if (showTotalDays) rowData.push(r.summary.total_days);
        if (showExpectedDays) rowData.push(r.summary.expected_days);
        if (showPresentDays) rowData.push(r.summary.present_days);
        if (showLeaveDays) rowData.push(r.summary.leave_days);

        return rowData;
      });

      autoTable(doc, {
        startY: 26,
        head: [tableHeaders],
        body: tableRows,
        theme: "grid",
        headStyles: { fillColor: [79, 70, 229], font: defaultFont, fontStyle: "bold" },
        styles: { fontSize: 8, font: defaultFont },
        columnStyles: {
          0: { cellWidth: 10 },
          1: { cellWidth: 25 },
          2: { cellWidth: 35 },
          3: { cellWidth: 25 },
        },
        didParseCell: function (data) {
          if (data.row.section === 'body' && data.column.index >= 4 && data.column.index < 4 + dates.length) {
            const val = data.cell.raw;
            if (val && typeof val === "string") {
              if (val.includes("-0.") || val.includes("-1")) {
                data.cell.styles.fillColor = [209, 250, 229]; // Emerald-100
                data.cell.styles.textColor = [6, 95, 70];
              } else if (val === "P H") {
                data.cell.styles.fillColor = [254, 226, 226]; // Red-100
                data.cell.styles.textColor = [153, 27, 27];
              } else if (val === "OFF") {
                data.cell.styles.fillColor = [243, 244, 246]; // Gray-100
                data.cell.styles.textColor = [75, 85, 99];
              }
            }
          }
        }
      });

      doc.save(`daily_attendance_matrix_${dateFrom}_${dateTo}.pdf`);
    } catch (e) {
      setError(e?.message || "PDF Export failed");
    }
  };

  const renderCellContent = (dayInfo) => {
    if (!dayInfo) return "-";
    if (dayInfo.type === "present" && displayMode === "presence") {
      return (
        <span style={{ color: "#10b981", fontWeight: 900, fontSize: "16px" }} title={dayInfo.text}>
          ✔
        </span>
      );
    }
    return dayInfo.text || "-";
  };

  const getCellStyles = (dayInfo) => {
    const base = { padding: "10px 8px", textAlign: "center", borderRight: "1px solid #e2e8f0" };
    if (!dayInfo) return base;

    if (dayInfo.type === "leave") {
      return {
        ...base,
        backgroundColor: "#d1fae5",
        color: "#065f46",
        fontWeight: 600
      };
    }
    if (dayInfo.type === "holiday") {
      return {
        ...base,
        backgroundColor: "#fee2e2",
        color: "#991b1b",
        fontWeight: 600
      };
    }
    if (dayInfo.type === "rest") {
      return {
        ...base,
        backgroundColor: "#f3f4f6",
        color: "#4b5563",
        fontStyle: "italic"
      };
    }
    return base;
  };

  return (
    <div style={{ minHeight: "100vh", backgroundColor: "#f8fafc" }}>
      <AppHeader />

      <div style={{ padding: "24px 32px", maxWidth: "100%", margin: "0 auto" }}>
        <h2 style={{ fontSize: "24px", fontWeight: 800, color: "#1e293b", marginBottom: 4 }}>
          Daily Attendance Matrix
        </h2>
        <p style={{ color: "#64748b", margin: "0 0 24px 0" }}>
          Aggregated matrix displaying daily attendance records, leave types, roster off days, and holidays.
        </p>

        {/* Filters Panel */}
        <div style={{
          backgroundColor: "#ffffff",
          padding: "20px 24px",
          borderRadius: 8,
          border: "1px solid #e2e8f0",
          display: "flex",
          flexDirection: "column",
          gap: 16,
          marginBottom: 24
        }}>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "end" }}>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", marginBottom: 6 }}>Date From</label>
              <input 
                type="date" 
                value={dateFrom} 
                onChange={(e) => setDateFrom(e.target.value)} 
                style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: "14px" }}
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", marginBottom: 6 }}>Date To</label>
              <input 
                type="date" 
                value={dateTo} 
                onChange={(e) => setDateTo(e.target.value)} 
                style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: "14px" }}
              />
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", marginBottom: 6 }}>Branch</label>
              <select 
                value={branchId} 
                onChange={(e) => setBranchId(e.target.value)}
                style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: "14px", minWidth: 150 }}
              >
                <option value="">All Branches</option>
                {branches.map(b => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", marginBottom: 6 }}>Department</label>
              <select 
                value={orgNodeId} 
                onChange={(e) => setOrgNodeId(e.target.value)}
                style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: "14px", minWidth: 160 }}
              >
                <option value="">All Departments</option>
                {departments.map(d => (
                  <option key={d.id} value={d.id}>
                    {'\u00a0'.repeat((d._depth || 0) * 2)}{d._depth > 0 ? '\u2514 ' : ''}{d.node_name}{d.level_type ? ` (${d.level_type})` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={{ display: "block", fontSize: "12px", fontWeight: 700, color: "#475569", marginBottom: 6 }}>Display Mode</label>
              <select 
                value={displayMode} 
                onChange={(e) => setDisplayMode(e.target.value)}
                style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #cbd5e1", fontSize: "14px", minWidth: 160 }}
              >
                <option value="time">Clock In Time</option>
                <option value="presence">Presence (Check Mark)</option>
              </select>
            </div>

            <div style={{ display: "flex", gap: 8 }}>
              <button 
                onClick={onSearch} 
                disabled={loading || !canSearch}
                style={{
                  backgroundColor: "primary.dark",
                  color: "#ffffff",
                  padding: "8px 16px",
                  borderRadius: 6,
                  border: "none",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                  opacity: (loading || !canSearch) ? 0.6 : 1
                }}
              >
                Search
              </button>
              <button 
                onClick={onExportExcel} 
                disabled={loading || rows.length === 0 || !canSearch}
                style={{
                  backgroundColor: "#10b981",
                  color: "#ffffff",
                  padding: "8px 16px",
                  borderRadius: 6,
                  border: "none",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                  opacity: (loading || rows.length === 0 || !canSearch) ? 0.6 : 1
                }}
              >
                Export Excel
              </button>
              <button 
                onClick={onExportPDF} 
                disabled={loading || rows.length === 0 || !canSearch}
                style={{
                  backgroundColor: "#e11d48",
                  color: "#ffffff",
                  padding: "8px 16px",
                  borderRadius: 6,
                  border: "none",
                  fontSize: "14px",
                  fontWeight: 600,
                  cursor: "pointer",
                  opacity: (loading || rows.length === 0 || !canSearch) ? 0.6 : 1
                }}
              >
                Export PDF
              </button>
            </div>
          </div>

          <div style={{ display: "flex", gap: 24, flexWrap: "wrap", borderTop: "1px solid #f1f5f9", paddingTop: 12 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "13px", fontWeight: 600, color: "#475569", cursor: "pointer" }}>
              <input type="checkbox" checked={showTotalDays} onChange={(e) => setShowTotalDays(e.target.checked)} />
              Total Days in Month
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "13px", fontWeight: 600, color: "#475569", cursor: "pointer" }}>
              <input type="checkbox" checked={showExpectedDays} onChange={(e) => setShowExpectedDays(e.target.checked)} />
              Expected Work Days
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "13px", fontWeight: 600, color: "#475569", cursor: "pointer" }}>
              <input type="checkbox" checked={showPresentDays} onChange={(e) => setShowPresentDays(e.target.checked)} />
              Present Days
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "13px", fontWeight: 600, color: "#475569", cursor: "pointer" }}>
              <input type="checkbox" checked={showLeaveDays} onChange={(e) => setShowLeaveDays(e.target.checked)} />
              Total Leave Days
            </label>
          </div>
        </div>

        {error && <div style={{ marginBottom: 16, color: "#ef4444", fontSize: "14px", fontWeight: 600 }}>{error}</div>}
        {loading && <div style={{ marginBottom: 16, color: "primary.dark", fontSize: "14px" }}>Loading report data...</div>}

        {/* Data Grid Table */}
        <div style={{
          backgroundColor: "#ffffff",
          borderRadius: 8,
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)"
        }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px", color: "#334155" }}>
              <thead>
                <tr style={{ backgroundColor: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, borderRight: "1px solid #e2e8f0" }}>No.</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, borderRight: "1px solid #e2e8f0" }}>Employee ID</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, borderRight: "1px solid #e2e8f0" }}>Staff Name</th>
                  <th style={{ padding: "12px 16px", textAlign: "left", fontWeight: 700, borderRight: "1px solid #e2e8f0" }}>Dept</th>
                  {dates.map((d) => (
                    <th key={d} style={{ padding: "12px 8px", textAlign: "center", fontWeight: 700, borderRight: "1px solid #e2e8f0", minWidth: 45 }}>
                      {d.split("-")[2]}
                    </th>
                  ))}
                  {showTotalDays && <th style={{ padding: "12px 12px", textAlign: "center", fontWeight: 700, borderRight: "1px solid #e2e8f0" }}>Total Days</th>}
                  {showExpectedDays && <th style={{ padding: "12px 12px", textAlign: "center", fontWeight: 700, borderRight: "1px solid #e2e8f0" }}>Expected</th>}
                  {showPresentDays && <th style={{ padding: "12px 12px", textAlign: "center", fontWeight: 700, borderRight: "1px solid #e2e8f0" }}>Present</th>}
                  {showLeaveDays && <th style={{ padding: "12px 12px", textAlign: "center", fontWeight: 700 }}>Leaves</th>}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, index) => (
                  <tr key={r.staff_code} style={{ borderBottom: "1px solid #f1f5f9" }}>
                    <td style={{ padding: "10px 16px", borderRight: "1px solid #e2e8f0" }}>{index + 1}</td>
                    <td style={{ padding: "10px 16px", fontWeight: 600, color: "#0f172a", borderRight: "1px solid #e2e8f0" }}>{r.employee_id}</td>
                    <td style={{ padding: "10px 16px", borderRight: "1px solid #e2e8f0" }}>{r.full_name}</td>
                    <td style={{ padding: "10px 16px", borderRight: "1px solid #e2e8f0" }}>{r.dept_name}</td>
                    {dates.map((d) => (
                      <td key={d} style={getCellStyles(r.days[d])}>
                        {renderCellContent(r.days[d])}
                      </td>
                    ))}
                    {showTotalDays && <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: 600, borderRight: "1px solid #e2e8f0" }}>{r.summary.total_days}</td>}
                    {showExpectedDays && <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: 600, borderRight: "1px solid #e2e8f0" }}>{r.summary.expected_days}</td>}
                    {showPresentDays && <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: 600, borderRight: "1px solid #e2e8f0", color: "#10b981" }}>{r.summary.present_days}</td>}
                    {showLeaveDays && <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: 600, color: "#f59e0b" }}>{r.summary.leave_days}</td>}
                  </tr>
                ))}

                {!loading && rows.length === 0 && (
                  <tr>
                    <td colSpan={dates.length + 4 + (showTotalDays?1:0) + (showExpectedDays?1:0) + (showPresentDays?1:0) + (showLeaveDays?1:0)} style={{ padding: "24px", textAlign: "center", color: "#64748b" }}>
                      No matching records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
